<template>
	<div class="new-add-employee">
		<!-- Stepper -->
		<stepper class="employee-stepper" :steps="steps" :current-step="step" :hide-on-last-step="false" @step-change="stepId => goToStep(stepId)"></stepper>
		<!-- Titles -->
		<h2 class="title is-2 pt-3" v-if="step === 1">{{ translations.page.subtitle.import }}</h2>
		<h2 class="title is-2" v-if="step === 2">{{ translations.page.subtitle.missing }}</h2>
		<h2 class="title is-2" v-if="step === 4">{{ translations.page.subtitle.overview }}</h2>

		<!-- Step 1 file import -->
		<div class="columns is-multiline" v-if="step === 1 && fileMode" :class="{'is-loading': employeeLoader.loading}">
			<div class="column is-full">
				<div class="is-3 is-pulled-left has-text-weight-bold pt-3">{{ translations.file.upload }}</div>
				<a :href="urls.template" class="button is-secondary is-pulled-right">
					<i class="fa-solid fa-download px-3"></i>
					{{ translations.file.download }}
				</a>
			</div>
			<new-file-import-button
				name="add-new-benefs"
				file-formats=".xlsx,.xls"
				:file-format-error="translations.file.errors.format"
				:multiple-files-error="translations.file.errors.too_many_files"
				:button-text="translations.file.browse"
				:pre-button-text="translations.file.drag_drop"
				:format-text="translations.file.formats"
				classes="button is-secondary mt-5 mb-2 px-5"
				@change="fileUpload"
			></new-file-import-button>
		</div>
		<!-- Step 1 manual employee and step 2 missing information -->
		<h2 class="title is-2 pt-3" v-if="step === 1 && !fileMode">{{ translations.page.subtitle.manual }}</h2>
		<div class="box" v-for="index in employees.length" v-if="canDisplayEmployee(employees[index - 1], index)">
			<div class="columns is-multiline">
				<div class="column is-full">
					<h3 class="title is-3 is-pulled-left">{{ translations.page.subtitle.employee }} {{ index }}</h3>
					<a @click="removeEmployee(index - 1)" class="button is-secondary is-pulled-right"><i class="fa-solid fa-trash"></i></a>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.firstname }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].firstname"
									required
									@input="checkRequired(index-1, 'firstname')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.firstname">{{ employees[index - 1].errors.firstname.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.lastname }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].lastname"
									required
									@input="checkRequired(index-1, 'lastname')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.lastname">{{ employees[index - 1].errors.lastname.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter" v-if="useNiss">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.niss }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].niss"
									required
									@input="extractBirthFromNiss(index - 1)"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.niss">{{ employees[index - 1].errors.niss.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.birth_date }}</label>
							<div class="control">
								<input
									class="input"
									type="date"
									v-model="employees[index - 1].birth_date"
									required
									@input="checkRequired(index-1, 'birth_date')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.birth_date">{{ employees[index - 1].errors.birth_date.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.language }}</label>
							<div class="select is-fullwidth">
								<select v-model="employees[index - 1].language" required>
									<option v-for="language in languages"
											:value="language" :key="language">
										{{ language.substring(0,2) }}</option>
								</select>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.language">{{ employees[index - 1].errors.language.join(', ') }}</p>
						</div>
					</div>
				</div>
				<!-- Disable card type for belgium -->
				<div class="column is-one-quarter" v-if="!useNiss">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.cardtype }}</label>
							<div class="select is-fullwidth">
								<select v-model="employees[index - 1].virtualCard" required :disabled="cardTypes.length === 1" @change="changeCardType(employees[index - 1])">
									<option v-for="cardType in cardTypes"
											:value="cardType === 'virtual'" :key="cardType">
										{{ cardType }}</option>
								</select>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.virtualCard">{{ employees[index - 1].errors.virtualCard.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label" :class="{'is-required': employees[index - 1].virtualCard}">{{ translations.fields.email }}</label>
							<div class="control">
								<input
									class="input"
									type="email"
									v-model="employees[index - 1].email"
									@input="validateEmail(index - 1)"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.email">{{ employees[index - 1].errors.email.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter" >
					<div class="control" >
						<div class="field">
							<label class="label">{{ translations.fields.customer_reference }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].customer_reference"
								>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="columns is-multiline" v-if="marketSettings.code === 'up_lux'">
				<div class="column is-full">
					<h4 class="title is-4 is-pulled-left">{{ translations.fields.additional_information }} <span class="tooltip is-tooltip-multiline" :data-tooltip="translations.fields.tooltip"><i class="fa fa-info-circle"></i></span></h4>
				</div>
				<div class="column is-one-quarter">
					<div class="control">
						<div class="field">
							<label class="label">{{ translations.fields.annual_workdays }}</label>
							<div class="control">
								<input
									class="input"
									type="number"
									v-model="employees[index - 1].annual_workdays"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.annual_workdays">
								{{ employees[index - 1].errors.annual_workdays.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label">{{ translations.fields.emv_voucher_value }}</label>
							<div class="control">
								<input
									class="input"
									type="number"
									v-model="employees[index - 1].emv_voucher_value"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.emv_voucher_value">
								{{ employees[index - 1].errors.emv_voucher_value.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label">{{ translations.fields.employer_contribution }}</label>
							<div class="control">
								<input
									class="input"
									type="number"
									v-model="employees[index - 1].employer_contribution"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.employer_contribution">
								{{ employees[index - 1].errors.employer_contribution.join(', ') }}</p>
						</div>
					</div>
				</div>
			</div>
			<div class="columns is-multiline" v-if="!employees[index - 1].virtualCard && homeDelivery">
				<div class="column is-full">
					<h3 class="title is-3 is-pulled-left">{{ translations.page.subtitle.address }}</h3>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.street }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].address_street"
									required
									@input="checkRequired(index-1, 'address_street')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.address_street">{{ employees[index - 1].errors.address_street.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.number }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].address_number"
									required
									@input="checkRequired(index-1, 'address_number')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.address_number">{{ employees[index - 1].errors.address_number.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label">{{ translations.fields.box }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].address_box"
								>
							</div>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.zip }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].address_zip"
									required
									@input="checkRequired(index-1, 'address_zip')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.address_zip">{{ employees[index - 1].errors.address_zip.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.city }}</label>
							<div class="control">
								<input
									class="input"
									type="text"
									v-model="employees[index - 1].address_city"
									required
									@input="checkRequired(index-1, 'address_city')"
								>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.address_city">{{ employees[index - 1].errors.address_city.join(', ') }}</p>
						</div>
					</div>
				</div>
				<div class="column is-one-quarter">
					<div class="control" >
						<div class="field">
							<label class="label is-required">{{ translations.fields.country }}</label>
							<div class="select is-fullwidth">
								<select v-model="employees[index - 1].address_country">
									<option v-for="country in countries" :value="country.country_id" :key="country.country_id">{{ country.name }}</option>
								</select>
							</div>
							<p class="help is-danger" v-if="employees[index - 1].errors.address_country">{{ employees[index - 1].errors.address_country.join(', ') }}</p>
						</div>
					</div>
				</div>
			</div>
			<div class="columns">
				<div class="column is-full">
					<small>{{ translations.fields.required }}</small>
				</div>
			</div>
		</div>

		<div v-if="(step === 1 && !fileMode) || (step === 2 && fileMode)">
			<a @click="addEmployee" class="button is-secondary"><i class="fa-solid fa-plus mr-2"></i>  {{ translations.button }}</a>
		</div>

		<!-- Step 3 card reoreder -->
		<div v-if="step === 3">
			<h2 class="title is-2">{{ translations.page.subtitle.cards }}</h2>
			<div class="" >
				<div class="column">
					<div class="control" >
						<div class="field">
							<div class="control has-icons-left">
								<input
									class="input"
									type="text"
									v-model="filterSearch"
									required
									placeholder="Search for employee name"
								>
								<span class="icon is-small is-left"><i class="fas fa-search"></i></span>
							</div>
						</div>
					</div>
				</div>
				<table class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
					<thead>
					<tr>
						<th> {{ translations.fields.firstname }} </th>
						<th> {{ translations.fields.lastname }} </th>
						<th> {{ translations.fields.birth_date }} </th>
						<th> {{ translations.fields.reorder_cards }} </th>
					</tr>
					</thead>
					<tbody>
					<tr v-for="index in employees.length" v-if="hasActiveAndFiltered(employees[index - 1])">
						<td> {{ employees[index - 1].firstname }} </td>
						<td> {{ employees[index - 1].lastname }} </td>
						<td> {{ employees[index - 1].birth_date }} </td>
						<td>
							<div class="" @click="toggleReorderCard(index)">
								<input
									type="checkbox"
									class="switch is-rtl"
									name="toggle-reorder-cards"
									v-model="employees[index - 1].needNewCard"
								>
								<label for="toggle-reorder-cards">&nbsp;</label>
							</div>
						</td>
					</tr>
					</tbody>
				</table>
			</div>
		</div>
		<!-- Step 3 modal -->
		<simple-modal
			@close="closeModal()"
			:display="showModal"
			:title="translations.reorder_modal.title"
		>
			<template #default>
				<div v-html="translations.reorder_modal.question.replace(':nbEmployees', employees.length).replace(':nb', nbRecordCard)">
				</div>
			</template>
			<template #footer>
				<button class="button" @click="skip">
					{{ translations.reorder_modal.skip }}
				</button>
				<button class="button" @click="closeModal">
					{{ translations.reorder_modal.cancel }}
				</button>
				<button
					class="button is-fullwidth is-success track-action"
					@click="submitModal()"
				>
					{{ translations.reorder_modal.confirm.replace(':nb', nbRecordCard) }}
				</button>
			</template>
		</simple-modal>
		<div class="box has-text-centered" v-if="displayNoData">
			<img src="/images/icons/no-data.svg" width="50%" />
		</div>
		<div class="notification is-info has-text-dark m-auto" v-if="step === 4">
			<i class="fa-solid fa-info px-3"></i> <span v-html="helpText"/>
		</div>
		<!-- Step 4 total summary -->
		<table class="table is-bordered is-striped is-narrow is-hoverable is-fullwidth" v-if="step === 4">
			<thead>
			<tr>
				<th> {{ translations.fees.title }} </th>
				<th> {{ translations.fees.employees }} </th>
				<th> {{ translations.fees.subtotal }} </th>
			</tr>
			</thead>
			<tbody>
			<tr v-for="fee in fees" v-if="fee.nb > 0">
				<td> {{ translations.fees[fee.code] }} </td>
				<td> {{ fee.nb }} </td>
				<td> € {{ (fee.total / 100).toFixed(2)  }} </td>
			</tr>
			<tr>
				<td colspan="2"><strong> {{ translations.fees['total'].toUpperCase() }} </strong></td>
				<td><strong> € {{ (total / 100).toFixed(2) }} </strong></td>
			</tr>
			</tbody>
		</table>
		<div class="is-pulled-right">
			<button class="button is-white" @click="skip" v-if="step=== 3 && !displayNoData">
				{{ translations.reorder_modal.skip }}
			</button>
			<button @click="nextStep" :disabled="loading" class="button is-orange">
				{{ loading ? translations.confirm_button_loading : translations.confirm_button }}
				<i class="fa-solid fa-spinner fa-spin ml-2" v-if="loading"></i>
			</button>		</div>
	</div>

</template>
<script>

import Stepper from "../stepper.vue";
import moment from "moment";
import {ApiMonizze, LoaderState} from "../../api-monizze";

export default {
	components: {Stepper},
	data: function () {
		return {
			employees: [],
			countries: [],
			loading: false,
			filterSearch: '',
			step:1,
			nbRecordCard:0,
			showModal:false,
			steps: [
				{
					id:1,
					title: this.translations.steps.employees
				},
				{
					id:2,
					title: this.translations.steps.compliance
				},
				{
					id:3,
					title: this.translations.steps.card_check
				},
				{
					id:4,
					title: this.translations.steps.summary
				}
			],
			employeeLoader: {
				loading: false
			},
		}
	},
	props: {
		employeeList: {
			type: Array,
			required: false,
			default: null
		},
		useNiss: {
			type: Boolean,
			required: true,
			default: false
		},
		languages: {
			type: Array,
			required: true,
		},
		cardTypes: {
			type: Array,
			required: true,
		},
		homeDelivery: {
			type: Boolean,
			required: true,
			default: false
		},
		customerParams: {
			type: Object,
			required: true,
		},
		translations: {
			type: Object,
			required: true,
		},
		urls: {
			type: Object,
			required: true,
		},
		fileMode: {
			type: Boolean,
			default: false,
		},
		marketSettings: {
			type: Object,
			required: true
		}
	},
	methods: {
		changeCardType : function (item) {
			if (item.virtualCard === false)
			{
				item.email = '';
			}
		},
		hasActiveAndFiltered : function (item)
		{
			let search = this.filterSearch.toLowerCase();
			return item.data?.has_active_card &&
					(item.firstname.toLowerCase().includes(search) || item.lastname.toLowerCase().includes(search));
		},
		extractBirthFromNiss: function (index)
		{
			let niss = this.employees[index].niss.replace(/\D/g, '');
			if (niss.length >= 6) {
				// Extract year, month, and day from NISS
				let year = parseInt(niss.substring(0, 2), 10);
				let month = parseInt(niss.substring(2, 4), 10) - 1; // Months are 0-based in JS Date
				let day = parseInt(niss.substring(4, 6), 10);

				// Determine century by checking if the year is greater than the current last two digits of the current year
				let currentYear = new Date().getFullYear();
				let century = (year > currentYear % 100) ? 1900 : 2000;

				// Create date object
				let birthDate = new Date(century + year, month, day);
				this.employees[index].birth_date = moment(birthDate).format('YYYY-MM-DD');
			}

			if (niss.length === 11) {
				if (this.employees[index].errors.hasOwnProperty('niss')) {
					delete this.employees[index].errors['niss'];
				}
			} else {
				if (!this.employees[index].errors.hasOwnProperty('niss')) {
					Object.defineProperty(this.employees[index].errors, "niss", { value: [this.translations.fields.errors.niss], configurable: true });
				}
			}
		},
		validateEmail: function (index)
		{
			if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(this.employees[index].email)) {
				if (this.employees[index].errors.hasOwnProperty('email')) {
					delete this.employees[index].errors['email'];
				}
			} else {
				if (!this.employees[index].errors.hasOwnProperty('email')){
					Object.defineProperty(this.employees[index].errors, "email", {value:[this.translations.fields.errors.email],  configurable: true});
				}
			}
		},
		checkRequired: function (index, field){
			if (this.employees[index][field] != '' ) {
				if (this.employees[index].errors.hasOwnProperty(field)) {
					delete this.employees[index].errors[field];
				}
			} else {
				if (!this.employees[index].errors.hasOwnProperty(field)){
					Object.defineProperty(
						this.employees[index].errors, field,
						{
							value:[
								this.translations.fields.errors.required.replace(':attribute', field)
									.replace(field, this.translations.fields.hasOwnProperty(field) ? this.translations.fields[field] : this.translations.fields[field.replace('address_', '')])
							],
							configurable: true
						}
					);
				}
			}
		},
		loadCountries: function()
		{
			new ApiMonizze()
				.getDataFromApi(
					this.urls.country,
					(data) => this.countries = data
				);
		},
		canDisplayEmployee: function (item, index){
			return (this.step === 1 && !this.fileMode) || (this.step === 2 && item.hasError)
		},
		initFormData: function () {
			return {
				firstname: '',
				lastname: '',
				birth_date: '',
				customer_reference: '',
				niss: '',
				language: this.customerParams['defaultLanguage'],
				virtualCard: this.cardTypes[0] === 'virtual',
				email:'',
				employer_contribution: null,
				emv_voucher_value: null,
				annual_workdays: null,
				address_box: '',
				address_city: '',
				address_country: this.customerParams['defaultCountry'],
				address_number: '',
				address_street: '',
				address_zip: '',
				errors: {},
				hasError: true,
				needNewCard:true,
				skip:false,
			}
		},
		addEmployee: function () {
			this.employees.push(this.initFormData());
		},
		removeEmployee: function (index) {
			this.employees.splice(index, 1);
		},
		validateInfo: async function () {
			this.loading = true;
			let validationUrl = this.urls.validate_user_info;
			let _this = this;
			let hasError = false;
			await axios.post(
				validationUrl,
				{
					rows: _this.employees
				},
				(new ApiMonizze()).addValidErrors([422])._defaultAxiosConfig()
			).then(data => {
				let response = data.data;
				let index = 0;
				response.forEach(item => {
					_this.employees[index].errors = {};
					this.employees[index].hasError = false;
					if (item.data !== null)
					{
						Object.assign(_this.employees[index], item.data);
						_this.employees[index].needNewCard = !_this.employees[index].data.exist;
						_this.employees[index].card_username = (_this.employees[index].firstname + ' ' + _this.employees[index].lastname).substring(0, 35);
						if (_this.employees[index].data.is_from_customer && !_this.employees[index].data.is_skipped)
						{
							hasError = true;
							this.employees[index].hasError = true;
							Object.defineProperty(
								this.employees[index].errors, this.useNiss ? 'niss' : 'birth_date',
								{
									value:[
										this.translations.fields.errors.already_exist
									],
									configurable: true
								}
							);
						}

					}
					if (item.errors !== null)
					{
						this.employees[index].hasError= true;
						let fields = Object.keys(item.errors);
						fields.forEach( field => {
							if (this.employees[index][field] === null || this.employees[index][field].toString().trim() === '')
							{
								Object.defineProperty(
									this.employees[index].errors, field,
									{
										value:[
											this.translations.fields.errors.required.replace(':attribute', field)
												.replace(field, this.translations.fields.hasOwnProperty(field) ? this.translations.fields[field] : this.translations.fields[field.replace('address_', '')])
										],
										configurable: true
									}
								);
							}
							else if(item.errors.hasOwnProperty(field))
							{
								Object.defineProperty(
									this.employees[index].errors, field,
									{
										value:[
											item.errors[field]
										],
										configurable: true
									}
								);
							}
						})

						hasError = true;
					}
					index++;
				})
			});
			this.loading = false;
			return hasError;
		},
		goToStep: function (stepId){
			this.step = stepId;
		},
		nextStep : async function () {
			let hasError = false;
			if (this.step < 3)
			{
				hasError = await this.validateInfo();
			}

			if (!hasError){
				if (this.step < this.steps.length)
				{
					this.step++;
					if (this.step === 2 && !hasError){
						this.step++;
					}
					if (this.step === 3) {
						this.checkReorderCard();
					}
				}
				else
				{
					this.submit();
				}
			}

		},
		toggleReorderCard: function (index)
		{
			this.employees[index - 1].needNewCard = !this.employees[index - 1].needNewCard
		},
		checkReorderCard : function (){
			let nb = 0;
			this.employees.forEach(item => {
				if (item.data.has_active_card)
				{
					nb++;
				}
			});
			this.nbRecordCard = nb;
			if (nb > 0)
			{
				if (nb > 1)
				{
					this.showModal = true;
				}
			}
			else
			{
				this.step++;
			}
		},
		closeModal: function (){
			this.showModal = false;
		},
		skip: function (){
			let _this = this;
			for (const item of this.employees) {
				const i = this.employees.indexOf(item);
				_this.employees[i].skip = true;
			}
			this.closeModal();
			this.nextStep();
		},
		submitModal: function () {
			let _this = this;
			for (const item of this.employees) {
				const i = this.employees.indexOf(item);
				_this.employees[i].needNewCard = true;
			}
			this.closeModal();
		},
		submit: function () {
			let _this = this;
			this.loading = true;
			let submitUrl = this.urls.submit;
			(new ApiMonizze())
				.postDataFromApi(
					submitUrl,
					{
						rows: _this.employees
					},
					function(data)
					{
						_this.loading = false;
						_this.$emit('finalize', _this.employees.length);
					}
				)
		},
		fileUpload: async function (fileInput) {
			let _this = this;
			this.employeeLoader.loading = true;
			this.employees = [];
			let uploadUrl = this.urls.upload_file;

			if (!fileInput.files.length)
			{
				return;
			}
			let formData = new FormData();
			formData.append('file', fileInput.files[0]);

			await (new ApiMonizze()).addValidErrors([422])
				.postDataFromApi(
					uploadUrl,
					formData,
					function(data, response)
					{
						if (response.errors)
						{
							cleanFlashAlert()
							addFlashAlert('danger', _this.translations.file.errors.format);
						}
						if (response.error)
						{
							cleanFlashAlert()
							addFlashAlert('danger', response.error);
						}
						else {
							response.data.forEach(item => {
								let employee = _this.initFormData();
								Object.assign(employee, item);
								_this.employees.push(employee);
							});
							_this.step++;
							_this.nextStep();
						}
						_this.employeeLoader.loading = false;
					}
				).catch(() => {
					_this.employeeLoader.loading = false;
				});
		},
	},
	computed: {
		helpText: function ()
		{
			return this.translations.fees.helptext.replace(':nbEmployees', this.employees.length);
		},
		hasErrors : function ()
		{
			let onError = this.employees.filter(function(item)
			{
				return item.hasError;
			})
			return onError.length > 0;
		},
		getEmployees : function(){
			if (this.step === 2)
			{
				return this.employees.filter(function(item)
				{
					return item.hasError;
				});
			}else
			{
				return this.employees;
			}

		},
		resultCount: function() {
			let employeeList = this.getEmployees;
			if (employeeList.length === 0) {
				return
			}
			return  employeeList.length
		},
		fees: function ()
		{
			let card_virtual = 0;
			let card = 0;
			let wallet = 0;
			this.employees.forEach(item => {
				if (item.needNewCard)
				{
					if (item.virtualCard) {
						card_virtual++;
					}else
					{
						card++;
					}
				}
				if (!item.data.exist)
				{
					wallet++;
				}
			})
			return [
				{'code' : 'card_new', 'nb' : card, 'total' : card * (this.customerParams.fees.hasOwnProperty('card_new') ? this.customerParams.fees['card_new'].value : 0)},
				{'code' : 'card_virtual', 'nb' : card_virtual, 'total' : card_virtual * (this.customerParams.fees.hasOwnProperty('card_virtual') ? this.customerParams.fees['card_virtual'].value : 0)},
				{'code' : 'act_wallet', 'nb' : wallet, 'total' : wallet * (this.customerParams.fees.hasOwnProperty('act_wallet') ? this.customerParams.fees['act_wallet'].value : 0)},
			];
		},
		total: function()
		{
			let fees = this.fees;
			let total = 0;
			fees.forEach(item => {
				total = total + item.total;
			})
			return total;
		},
		displayNoData: function ()
		{
			return (this.step === 2 && !this.hasErrors) || (this.step === 3 && this.nbRecordCard === 0)
		}
	},
	created() {
		this.loadCountries();
		if (this.employeeList.length === 0)
		{
			this.addEmployee();
		}
		else
		{
			this.employees = this.employeeList;
		}

	}
}
</script>
<style>
.employee-stepper {
	.step-indicator li:last-child div.step-separator{
		display:none;
	}
	.step-indicator li {
		width:32%;
	}
	.step-indicator li .step{
		margin:0;
		width: 35px;
	}
	.step-indicator li .caption{
		margin: 0 0 0 15px;
		width:80%;
	}
}
.pagination-first::after ,
.pagination-last::before {
	content:' ... ';
	margin: 10px;
	vertical-align: bottom;
}
@media screen and (min-width: 769px), print {
	.new-add-employee {
		.modal-content, .modal-card {
			width: 840px !important;
		}
	}

}
</style>
